import { useNavigate } from 'react-router-dom';
import { useLocalization } from '@/hooks/useLocalization';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUserRole } from '@/hooks/useUserRole';
import { useTenant } from '@/hooks/useTenant';
import { useUnreadEmailCount } from '@/hooks/useUnreadEmailCount';
import { useConnectedSources } from '@/hooks/useConnectedSources';
import { useAttention } from '@/hooks/useAttention';
import { getRoleDisplayName, type AppRole } from '@/lib/permissions';
import { sectionForView, visibleSections, type NavSection } from '@/lib/navigation';
import {
  Sun, Inbox, Rocket, Scale, ShieldCheck, Settings, Plug, Plus,
  type LucideIcon,
} from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';

const SECTION_ICONS: Record<NavSection['icon'], LucideIcon> = {
  today: Sun,
  communicate: Inbox,
  deliver: Rocket,
  decide: Scale,
  govern: ShieldCheck,
  admin: Settings,
};

const STATUS_DOT: Record<string, string> = {
  ok: 'bg-success',
  warn: 'bg-warning',
  off: 'bg-muted-foreground/50',
};
const STATUS_TEXT: Record<string, string> = { ok: 'Connected', warn: 'Needs attention', off: 'Paused' };

interface SidebarProps {
  activeView: string;
  onViewChange: (view: string) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const SidebarContent = ({
  activeView,
  onViewChange,
  onItemClick,
  userRole,
}: {
  activeView: string;
  onViewChange: (view: string) => void;
  onItemClick?: () => void;
  userRole: AppRole | null;
}) => {
  const navigate = useNavigate();
  const { tenantSlug } = useTenant();
  const { count: unreadEmailCount } = useUnreadEmailCount();
  const { data: sources = [], isLoading: sourcesLoading } = useConnectedSources();
  const attention = useAttention();
  const { tx, t } = useLocalization();

  const tenantPath = (suffix: string) => (tenantSlug ? `/${tenantSlug}${suffix}` : suffix);
  const go = (path: string) => { navigate(path); onItemClick?.(); };
  const pick = (view: string) => { onViewChange(view); onItemClick?.(); };

  const sections = visibleSections(userRole);
  const main = sections.filter((s) => !s.footer);
  const footer = sections.filter((s) => s.footer);
  const activeSectionKey = sectionForView(activeView)?.key;

  const itemClass = (active: boolean) =>
    cn(
      'masira-sidebar-item w-full',
      active && 'masira-sidebar-item-active',
    );

  const renderSection = (s: (typeof sections)[number]) => {
    const Icon = SECTION_ICONS[s.icon];
    const active = activeSectionKey === s.key;
    const attn = attention.bySection[s.key];
    const badge = s.key === 'communicate' ? unreadEmailCount : attn?.count ?? 0;
    const urgent = !!attn?.urgent;
    const badgeLabel = s.key === 'communicate' ? 'unread' : s.key === 'decide' ? 'waiting for you' : 'open';
    return (
      <button
        key={s.key}
        type="button"
        onClick={() => pick(active ? activeView : s.landing)}
        aria-current={active ? 'page' : undefined}
        data-nav-section={s.key}
        className={itemClass(active)}
      >
        <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
        <span className="flex-1 text-start">{t(s.label)}</span>
        {badge > 0 && (
          <span
            className={cn(
              'ms-auto min-w-5 rounded-full px-1.5 text-center font-mono text-[11px] tabular-nums',
              urgent ? 'bg-destructive text-destructive-foreground' : 'bg-white/15',
            )}
            aria-label={`${badge} ${badgeLabel}${urgent ? ', some critical' : ''}`}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>
    );
  };

  const connectedCount = sources.filter((s) => s.status !== 'off').length;

  return (
    <>
      <div className="h-16 flex items-center px-5 border-b border-[hsl(var(--sidebar-border))]">
        <img src="/masira-logo-word-mark-dark-theme.svg" alt={t('Masira')} className="h-8 w-auto" />
      </div>

      <nav aria-label={t('Primary')} className="p-3 space-y-1">
        <p className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-white/50 font-heading">
          {t('Console')}
        </p>
        {main.map(renderSection)}
      </nav>

      <div className="flex-1 min-h-0 overflow-y-auto masira-scrollbar px-3 pb-3 border-t border-[hsl(var(--sidebar-border))] mt-2">
        <div className="flex items-center justify-between px-3 pt-4 pb-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-white/50 font-heading">
            {t('Connected sources')}
          </p>
          {sources.length > 0 && (
            <span className="font-mono text-[11px] text-white/60">{connectedCount}</span>
          )}
        </div>
        {sourcesLoading ? (
          <p className="px-3 py-2 text-xs text-white/50">{t('Checking connections…')}</p>
        ) : sources.length === 0 ? (
          <button
            type="button"
            onClick={() => go(tenantPath('/integrations'))}
            className="mx-1 mt-1 w-[calc(100%-0.5rem)] rounded-lg border border-dashed border-white/25 px-3 py-3 text-start text-sm text-white/80 hover:bg-[hsl(var(--masira-sidebar-hover))] transition-colors"
          >
            <span className="flex items-center gap-2 font-semibold font-heading">
              <Plus className="w-4 h-4" aria-hidden="true" />  {t('Connect your tools')}
            </span>
            <span className="mt-1 block text-xs text-white/55">
              
              {t('Jira, Microsoft 365, Azure DevOps and more. Masira reads, you stay in control.')}
            </span>
          </button>
        ) : (
          <ul className="space-y-0.5">
            {sources.map((src) => (
              <li
                key={src.id}
                className="flex items-center gap-3 rounded-md px-3 py-1.5 text-sm text-white/80"
                title={`${src.name}: ${t(STATUS_TEXT[src.status])}`}
              >
                <Plug className="w-4 h-4 text-white/60" aria-hidden="true" />
                <span className="flex-1 truncate">{src.name}</span>
                <span className={cn('h-2 w-2 rounded-full', STATUS_DOT[src.status])} />
                <span className="sr-only">{t(STATUS_TEXT[src.status])}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="p-3 border-t border-[hsl(var(--sidebar-border))] space-y-1">
        <button type="button" onClick={() => go(tenantPath('/integrations'))} className={itemClass(activeView === 'integrations')}>
          <Plug className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <span className="flex-1 text-start">{t('Integrations')}</span>
        </button>
        {footer.map(renderSection)}
        {userRole && (
          <p className="px-3 pt-2 text-xs text-white/45">
            {t('Signed in as')} <span className="text-white/70">{tx(getRoleDisplayName(userRole))}</span>
          </p>
        )}
      </div>
    </>
  );
};

const Sidebar = ({ activeView, onViewChange, isOpen = false, onOpenChange }: SidebarProps) => {
  const isMobile = useIsMobile();
  const { role } = useUserRole();

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent
          side="left"
          className="w-72 p-0 border-[hsl(var(--sidebar-border))] bg-[hsl(var(--masira-sidebar))] text-white flex flex-col"
        >
          <SidebarContent
            activeView={activeView}
            onViewChange={onViewChange}
            onItemClick={() => onOpenChange?.(false)}
            userRole={role}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside className="fixed top-0 start-0 z-50 h-screen w-60 flex flex-col border-e border-[hsl(var(--sidebar-border))] bg-[hsl(var(--masira-sidebar))] text-white">
      <SidebarContent activeView={activeView} onViewChange={onViewChange} userRole={role} />
    </aside>
  );
};

export default Sidebar;
